import { createBrowserHistory, createPath, parsePath } from "history";
import { DATE_PATH_FORMAT } from "./dates";
import { DateTime } from "luxon";
import { ElementType, ReactNode } from "react";
import { toQuery } from "common/utils/browser";

export const history = createBrowserHistory();

interface Props {
  [index: string]: any;
}

interface RenderProps extends Props {
  render: () => ReactNode;
}

interface ComponentProps extends Props {
  component: ElementType;
}

export type RouteProps = RenderProps | ComponentProps;

// Gets a URL ecoded pathname for the current page
export const getSafePath = (): string =>
  encodeURIComponent(window.location.pathname);

export enum Slug {
  ID = ":id",
  NEW = "new",
  EDIT = "edit",
  REPORT = ":report",
  REPORT_INSTANCE = ":instance",
  SERIAL = ":serial",
}

/** Shared search-param key for page tabs (`?tab=`). */
export const TAB_QUERY = "tab";

/** Shared search-param key for customer scope (`?customer=`). */
export const CUSTOMER_QUERY = "customer";

export enum Path {
  AUTOTRACTOR = "/atk",
  ACCOUNT = "/account",
  ADMIN = "/admin",
  BASE = "/",
  CALLBACK = "/callback",
  CARBON_CROPS = "/carbon-crops",
  CATEGORY_TEMPLATES = "/category-templates",
  CUSTOMERS = "/customers",
  EQUIPMENT = `${Path.AUTOTRACTOR}/equipment`,
  FARMS = `/farms`,
  JOBS = `${Path.AUTOTRACTOR}/jobs`,
  STARTUP_CHECKLISTS = `${Path.AUTOTRACTOR}/startup-checklists`,
  FLEET = "/fleet",
  GDPR_CONSENT = "/consent",
  KNOWLEDGE = "/knowledge",
  LOGIN = "/login",
  LOGOUT = "/logout",
  OFFLINE = "/offline",
  PLANT_PROFILES = "/plant-profiles",
  REPORTS = "/reports",
  ROBOTS = `${Path.FLEET}/robots`,
  SETTINGS = "/settings",
  USERS = "/users",
}

export enum RobotSubpath {
  ADMIN = "/admin",
  ALARMS = "/alarms",
  ALMANAC = "/almanac",
  AUTOTRACTOR = "/autotractor",
  PLANT_PROFILE = "/plant-profile",
  CONFIG = "/config",
  CROPS = "/crops",
  DISCRIMINATOR = "/discriminator",
  FIELDS = "/fields",
  HARDWARE = "/hardware",
  HARDWARE_COMPUTERS = "/hardware/computers",
  HARDWARE_VERSIONS = "/hardware/versions",
  HARDWARE_LASERS = "/hardware/lasers",
  HARDWARE_WEEDING_MODULES = "/hardware/modules",
  HARDWARE_WEEDING_MODULES_HISTORY = "/hardware/modules/history",
  HISTORY = "/history",
  SUMMARY = "/",
  SUPPORT = "/support",
  UPLOADS = "/uploads",
  VELOCITY_ESTIMATOR = "/velocity",
}

export enum EquipmentSubpath {
  IMPLEMENTS = "/implements",
  IMPLEMENT_ARCHETYPES = "/implement-archetypes",
  SWITCH_RIG = "/switch-rig",
  NEW_RIG = "/new-rig",
  RIG_DETAILS = "/rig-details",
  TRACTORS = "/tractors",
  TRACTOR_ARCHETYPES = "/tractor-archetypes",
  CREATE_TRACTOR_ARCHETYPE = "/tractors/create-archetype",
  CREATE_TRACTOR = "/tractors/create",
  CREATE_IMPLEMENT = "/implements/create",
  EDIT_IMPLEMENT = `/implements/${Slug.EDIT}`,
  EDIT_TRACTOR = `/tractors/${Slug.EDIT}`,
  CREATE_IMPLEMENT_ARCHETYPE = "/implements/create-archetype",
  EDIT_IMPLEMENT_ARCHETYPE = `/implement-archetypes/${Slug.EDIT}`,
  EDIT_TRACTOR_ARCHETYPE = `/tractor-archetypes/${Slug.EDIT}`,
}

export enum AutotractorSubpath {
  JOBS = "/jobs",
  FARMS = "/farms",
}

export enum FleetSubpath {
  ALARMS = "/alarms",
  HARDWARE = "/hardware",
  HARDWARE_LASERS = "/hardware/lasers",
  HARDWARE_WEEDING_MODULES = "/hardware/modules",
  ROBOTS = "/robots",
  MISSION_CONTROL = "/mission-control",
}

export enum ReportSubpath {
  SCHEDULED = "/scheduled",
  JOB_INSIGHTS = "/job-insights",
}

export enum AdminSubpath {
  ALARMS = "/alarms",
  ALMANAC = "/almanac",
  CARBON_CROPS = "/carbon-crops",
  PLANT_PROFILE = "/plant-profile",
  CONFIG = "/config", // moved to CONFIG_TEMPLATES
  CONFIG_BULK = "/config/bulk",
  CONFIG_TEMPLATES = "/config/templates",
  DISCRIMINATOR = "/discriminator",
  PORTAL = "/portal",
  VELOCITY_ESTIMATOR = "/velocity",
}

export const getCustomerPath = (id: string = Slug.ID): string =>
  `${Path.CUSTOMERS}/${id}`;

export const getReportPath = (slug: string = Slug.REPORT): string =>
  `${Path.REPORTS}${ReportSubpath.SCHEDULED}/${slug}`;

export const getCropPath = (serial: string, cropId: string): string =>
  `${getRobotPath(serial, RobotSubpath.CROPS)}/${cropId}`;

export const getModelinatorPath = (
  serial: string,
  cropId: string,
  modelId: string
): string => `${getCropPath(serial, cropId)}/${modelId}`;

export const getAlmanacPath = (serial: string, uuid: string): string =>
  `${getRobotPath(serial, RobotSubpath.ALMANAC)}/${uuid}`;

export const getDiscriminatorPath = (serial: string, uuid: string): string =>
  `${getRobotPath(serial, RobotSubpath.DISCRIMINATOR)}/${uuid}`;

export const getReportInstancesPath = (slug: string = Slug.REPORT): string =>
  `${Path.REPORTS}${ReportSubpath.SCHEDULED}/${slug}/runs`;

export const getReportInstancePath = (
  reportSlug: string = Slug.REPORT,
  instanceSlug: string = Slug.REPORT_INSTANCE
): string =>
  `${Path.REPORTS}${ReportSubpath.SCHEDULED}/${reportSlug}/runs/${instanceSlug}`;

export const getRobotPath = (
  serial: string = Slug.SERIAL,
  subpage?: RobotSubpath | undefined
): string =>
  `${Path.ROBOTS}/${serial}${serial === Slug.SERIAL ? "/*" : subpage ?? ""}`;

export const getRobotHistoryPath = (
  serial: string = Slug.SERIAL,
  jobId: string
): string => {
  const today = DateTime.local();
  const lastWeek = today.minus({ days: 7 });
  return `${getRobotPath(serial, RobotSubpath.HISTORY)}/${lastWeek.toFormat(
    DATE_PATH_FORMAT
  )}/${today.toFormat(DATE_PATH_FORMAT)}?${toQuery({ jobId })}`;
};

export const getRobotActivePlantProfilePath = (serial?: string): string =>
  getRobotPath(serial ?? Slug.SERIAL, RobotSubpath.PLANT_PROFILE);

/** Inserts `/edit` before any existing query string. */
export const getPlantProfileEditPath = (viewPath: string): string => {
  const { pathname = "", search, hash } = parsePath(viewPath);
  return createPath({ pathname: `${pathname}/${Slug.EDIT}`, search, hash });
};

const withCustomerQuery = (path: string, customerUuid?: string): string => {
  if (!customerUuid) {
    return path;
  }
  return `${path}?${toQuery({ [CUSTOMER_QUERY]: customerUuid })}`;
};

export const getPlantProfilesPath = (customerUuid?: string): string =>
  withCustomerQuery(Path.PLANT_PROFILES, customerUuid);

export const getCustomerPlantProfilePath = (
  plantProfileId: string,
  customerUuid?: string
): string =>
  withCustomerQuery(`${Path.PLANT_PROFILES}/${plantProfileId}`, customerUuid);

export const getCustomerPlantProfileEditPath = (
  plantProfileId: string,
  customerUuid?: string
): string =>
  getPlantProfileEditPath(
    getCustomerPlantProfilePath(plantProfileId, customerUuid)
  );

export const getCategoryTemplatesPath = (
  categoryId?: string,
  customerUuid?: string
): string =>
  withCustomerQuery(
    categoryId
      ? `${Path.CATEGORY_TEMPLATES}/${categoryId}`
      : Path.CATEGORY_TEMPLATES,
    customerUuid
  );

export const getCategoryTemplateEditPath = (categoryId: string): string =>
  `${getCategoryTemplatesPath(categoryId)}/${Slug.EDIT}`;

export const isCategoryTemplateEditPath = (pathname: string): boolean =>
  pathname.startsWith(Path.CATEGORY_TEMPLATES) &&
  pathname.endsWith(`/${Slug.EDIT}`);

export const getFleetPath = (subpage?: FleetSubpath | undefined): string =>
  `${Path.FLEET}${subpage ?? "/*"}`;

export const getAdminPath = (subpage?: AdminSubpath | undefined): string =>
  `${Path.ADMIN}${subpage ?? "/*"}`;

export const getAdminPlantProfilePath = (id?: string): string =>
  id
    ? `${getAdminPath(AdminSubpath.PLANT_PROFILE)}/${id}`
    : getAdminPath(AdminSubpath.PLANT_PROFILE);

export const isPlantProfileEditPath = (pathname: string): boolean =>
  pathname.endsWith(`/${Slug.EDIT}`) &&
  (pathname.startsWith(`${Path.PLANT_PROFILES}/`) ||
    pathname.startsWith(`${getAdminPath(AdminSubpath.PLANT_PROFILE)}/`) ||
    pathname.includes(`${RobotSubpath.PLANT_PROFILE}/`));

export const getAdminCarbonCropPath = (id?: string): string =>
  id
    ? `${getAdminPath(AdminSubpath.CARBON_CROPS)}/${id}`
    : getAdminPath(AdminSubpath.CARBON_CROPS);

export const getCarbonCropsPath = (customerUuid?: string): string =>
  withCustomerQuery(Path.CARBON_CROPS, customerUuid);

export const getUserPath = (id: string = Slug.ID): string =>
  `${Path.USERS}/${id}`;

export const getFarmPath = (farmId: string): string =>
  `${Path.FARMS}/${farmId}`;

export const getJobPath = (jobId: string): string => `${Path.JOBS}/${jobId}`;

export const getStartupChecklistDefinitionPath = (
  checklistDefinitionId: string
): string => `${Path.STARTUP_CHECKLISTS}/${checklistDefinitionId}`;

export const getJobOrchestratePath = (jobId: number): string =>
  `${Path.JOBS}/${jobId}/orchestrate`;

export const getTractorPath = (tractorId: number): string =>
  `${Path.EQUIPMENT}${EquipmentSubpath.TRACTORS}/${tractorId}`;

export const getImplementPath = (implementId: number): string =>
  `${Path.EQUIPMENT}${EquipmentSubpath.IMPLEMENTS}/${implementId}`;

export const getTractorsPath = (): string =>
  `${Path.EQUIPMENT}${EquipmentSubpath.TRACTORS}`;

export const getImplementsPath = (): string =>
  `${Path.EQUIPMENT}${EquipmentSubpath.IMPLEMENTS}`;

export const getTractorArchetypesPath = (): string =>
  `${Path.EQUIPMENT}${EquipmentSubpath.TRACTOR_ARCHETYPES}`;

export const getImplementArchetypesPath = (): string =>
  `${Path.EQUIPMENT}${EquipmentSubpath.IMPLEMENT_ARCHETYPES}`;

export const getSwitchRigPath = (serial: string): string =>
  `${Path.EQUIPMENT}${EquipmentSubpath.SWITCH_RIG}/${serial}`;

export const getNewRigPath = (serial: string): string =>
  `${Path.EQUIPMENT}${EquipmentSubpath.NEW_RIG}/${serial}`;

export const getRigDetailsPath = (rigId: string): string =>
  `${Path.EQUIPMENT}${EquipmentSubpath.RIG_DETAILS}/${rigId}`;

export const getCreateTractorPath = (setDefaultOnSerial?: string): string =>
  setDefaultOnSerial
    ? `${Path.EQUIPMENT}${EquipmentSubpath.CREATE_TRACTOR}?${toQuery({
        setDefaultOnSerial,
      })}`
    : `${Path.EQUIPMENT}${EquipmentSubpath.CREATE_TRACTOR}`;

export const getCreateImplementPath = (): string =>
  `${Path.EQUIPMENT}${EquipmentSubpath.CREATE_IMPLEMENT}`;

export const getEditImplementPath = (implementId: string): string =>
  `${Path.EQUIPMENT}${EquipmentSubpath.EDIT_IMPLEMENT}/${implementId}`;

export const getEditTractorPath = (tractorId: string): string =>
  `${Path.EQUIPMENT}${EquipmentSubpath.EDIT_TRACTOR}/${tractorId}`;

export const getEditImplementArchetypePath = (archetypeId: string): string =>
  `${Path.EQUIPMENT}${EquipmentSubpath.EDIT_IMPLEMENT_ARCHETYPE}/${archetypeId}`;

export const getEditTractorArchetypePath = (archetypeId: string): string =>
  `${Path.EQUIPMENT}${EquipmentSubpath.EDIT_TRACTOR_ARCHETYPE}/${archetypeId}`;
